import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Copy, Check, ChevronDown, ChevronRight, Clock, Hash, Coins, Cpu,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import type { AnalysisReport } from "@/api/serp-analysis";

// Highlight ${variable} patterns in prompt text
function HighlightedPrompt({ text }: { text: string }) {
  const parts = text.split(/(\$\{[^}]+\})/g);
  return (
    <pre className="whitespace-pre-wrap break-words text-sm font-mono leading-relaxed">
      {parts.map((part, i) =>
        part.startsWith("${") ? (
          <span key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </pre>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success("已複製 Copied");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Button size="sm" variant="ghost" onClick={handleCopy} className="h-7 px-2 text-xs">
      {copied ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
      {label ?? "複製 Copy"}
    </Button>
  );
}

// Collapsible prompt section
function PromptSection({
  title,
  content,
  defaultOpen = false,
}: {
  title: string;
  content: string | null;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);

  if (!content) {
    return (
      <div className="border rounded-lg p-3 bg-muted/30">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <ChevronRight className="h-4 w-4" />
          <span>{title}</span>
          <Badge variant="outline" className="text-xs">empty</Badge>
        </div>
      </div>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="border rounded-lg overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="flex items-center justify-between w-full p-3 hover:bg-muted/50 transition-colors text-left">
            <div className="flex items-center gap-2">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <span className="font-medium text-sm">{title}</span>
              <Badge variant="outline" className="text-xs">
                {content.length.toLocaleString()} chars
              </Badge>
            </div>
            <CopyButton text={content} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t p-3 bg-muted/20 max-h-80 overflow-auto">
            <HighlightedPrompt text={content} />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

interface AiLogModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: AnalysisReport;
  stepLabel: string;
}

export default function AiLogModal({ open, onOpenChange, report, stepLabel }: AiLogModalProps) {
  const statusColor = report.status === "completed"
    ? "bg-green-600"
    : report.status === "failed"
      ? "bg-red-600"
      : "bg-blue-600";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            AI Log — {stepLabel}
            <Badge className={statusColor}>{report.status}</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto pr-2">
          <div className="space-y-4">
            {/* Section 1: Metadata */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 border rounded-lg bg-muted/30">
              {report.ai_model && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Model:</span>
                  <span className="font-mono text-xs">{report.ai_model}</span>
                </div>
              )}
              {report.duration_ms != null && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Duration:</span>
                  <span>{(report.duration_ms / 1000).toFixed(1)}s</span>
                </div>
              )}
              {(report.tokens_input != null || report.tokens_output != null) && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Tokens:</span>
                  <span>{(report.tokens_input ?? 0).toLocaleString()} in / {(report.tokens_output ?? 0).toLocaleString()} out</span>
                </div>
              )}
              {report.cost != null && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Coins className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Cost:</span>
                  <span>{report.cost.toFixed(4)} Credits</span>
                </div>
              )}
              {report.prompt_version != null && (
                <div className="flex items-center gap-1.5 text-sm">
                  <span className="text-muted-foreground">Prompt:</span>
                  <span>v{report.prompt_version}{report.prompt_id != null ? ` (id=${report.prompt_id})` : ""}</span>
                </div>
              )}
            </div>

            {/* Section 2: System Prompt */}
            <PromptSection
              title="系統指令 System Prompt"
              content={report.system_prompt}
              defaultOpen={false}
            />

            {/* Section 3: User Prompt */}
            <PromptSection
              title="使用者指令 User Prompt"
              content={report.user_prompt}
              defaultOpen={false}
            />

            {/* Section 4: AI Output */}
            <div className="border rounded-lg overflow-hidden">
              <div className="flex items-center justify-between p-3">
                <span className="font-medium text-sm">AI 回應 Output</span>
                {report.report && <CopyButton text={report.report} label="複製原始文字 Copy raw" />}
              </div>
              <div className="border-t p-3 bg-muted/20">
                {report.report ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.report}</ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm text-center py-4">No output</p>
                )}
              </div>
              {report.report_json && (
                <div className="border-t p-3 bg-muted/30">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground font-medium">JSON Output</span>
                    <CopyButton text={JSON.stringify(report.report_json, null, 2)} label="複製 JSON" />
                  </div>
                  <pre className="text-xs font-mono whitespace-pre-wrap break-words max-h-60 overflow-auto">
                    {JSON.stringify(report.report_json, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
